import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { updatePassword } from "api/graphQL_users";
import DivLoader from "components/divloader/DivLoader";
import InputField from "components/fields/InputField";
import React from "react";
import { toast } from "react-hot-toast";
import { MdHighlightOff } from "react-icons/md";

const ChangePasswordModal = () => {
  const rootForm = { old_password: "", new_password: "" };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [passwordShow, setPasswordShow] = React.useState(false);
  const [passwordShowNew, setPasswordShowNew] = React.useState(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setFormValues(rootForm);
    setFormValuesErr({});
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  function handleValueChange(e: any) {
    setFormValues((p: any) => ({ ...p, [e.target.id]: e.target.value }));
    setFormValuesErr((p: any) => ({ ...p, [e.target.id]: "" }));
  }

  function validateData() {
    let errors: any = {};
    if (!formValues.old_password?.trim()) errors.old_password = "The field is required";
    else if (formValues.old_password.length < 8) errors.old_password = "Password must be at least 8 characters";
    if (!formValues.new_password?.trim()) errors.new_password = "The field is required";
    else if (formValues.new_password.length < 8) errors.new_password = "Password must be at least 8 characters";
    setFormValuesErr(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    try {
      setIsLoading(true);
      const result = await updatePassword(formValues);
      if (result.status === "Error") toast.error(result?.message ?? "Unable to update password");
      else { toast.success("Password changed successfully"); handleClose(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to update password");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <button className="prof-action-btn" onClick={onOpen}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Change Password
      </button>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`prof-panel${isOpen && !isCloseModal ? " prof-panel--open" : " prof-panel--close"}`}>
          <div className="prof-glow prof-glow--blue" />

          <div className="prof-header prof-header--blue">
            <div className="prof-header-brand">
              <div className="prof-header-logo">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div>
                <h2 className="prof-title">Change Password</h2>
                <p className="prof-sub">Update your account password</p>
              </div>
            </div>
            <button className="prof-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          <div className="prof-body">
            <div className="prof-field-group">
              <InputField
                variant="auth" extra="w-full"
                label="Current Password *" placeholder="••••••••"
                id="old_password"
                value={formValues?.old_password}
                errMessage={formValuesErr?.old_password}
                onChange={handleValueChange}
                type={passwordShow ? "text" : "password"}
                passwordShow={passwordShow}
                setPasswordShow={setPasswordShow}
              />
            </div>
            <div className="prof-field-group">
              <InputField
                variant="auth" extra="w-full"
                label="New Password *" placeholder="••••••••"
                id="new_password"
                value={formValues?.new_password}
                errMessage={formValuesErr?.new_password}
                onChange={handleValueChange}
                type={passwordShowNew ? "text" : "password"}
                passwordShow={passwordShowNew}
                setPasswordShow={setPasswordShowNew}
              />
            </div>
            <div className="prof-tip">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Use at least 8 characters, including letters and numbers
            </div>
          </div>

          <div className="prof-footer">
            <button className="prof-btn-cancel" onClick={handleClose}>Cancel</button>
            <button className="prof-btn-submit--blue" onClick={handleSubmit} disabled={!!isLoading}>
              {isLoading ? <span className="prof-spinner" /> : (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Update Password</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {PROF_STYLES}
    </>
  );
};

export default ChangePasswordModal;

/* ── Shared profile panel styles ── */
export const PROF_STYLES = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    /* Trigger button */
    .prof-action-btn {
      display: inline-flex; align-items: center; gap: 7px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 13px; font-weight: 700;
      padding: 9px 16px; border-radius: 10px; cursor: pointer;
      border: 1.5px solid #bfdbfe; background: #eff6ff; color: #2563eb;
      transition: all .15s;
    }
    .prof-action-btn:hover { background: #dbeafe; border-color: #93c5fd; }
    .prof-action-btn--red { border-color: #fecdd3; background: #fff1f2; color: #e11d48; }
    .prof-action-btn--red:hover { background: #ffe4e6; border-color: #fda4af; }

    /* Panel */
    .prof-panel {
      font-family: 'Plus Jakarta Sans', sans-serif;
      position: fixed; top: 0; right: 0; bottom: 0;
      width: 100%; max-width: 520px;
      background: #fff; z-index: 1004;
      display: flex; flex-direction: column;
      box-shadow: -12px 0 60px rgba(15,23,42,.18);
      transition: transform .35s cubic-bezier(0.16,1,0.3,1);
      overflow: hidden;
    }
    .prof-panel--open  { transform: translateX(0); }
    .prof-panel--close { transform: translateX(100%); }

    .prof-glow { height: 3px; flex-shrink: 0; background-size: 200% 100%; animation: profGlow 3s linear infinite; }
    .prof-glow--blue   { background: linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744); }
    .prof-glow--red    { background: linear-gradient(90deg,#7f1d1d,#dc2626,#ef4444,#dc2626,#7f1d1d); }
    @keyframes profGlow { 0%{background-position:0%} 100%{background-position:200%} }

    .prof-header { padding: 15px 24px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
    .prof-header--blue   { background: linear-gradient(135deg,#0f2744 0%,#1d4ed8 60%,#2563eb 100%); }
    .prof-header--red    { background: linear-gradient(135deg,#7f1d1d 0%,#b91c1c 60%,#dc2626 100%); }

    .prof-header-brand { display: flex; align-items: center; gap: 13px; }
    .prof-header-logo { width: 42px; height: 42px; border-radius: 12px; background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .prof-title  { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
    .prof-sub    { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
    .prof-close  { background: none; border: none; cursor: pointer; color: rgba(255,255,255,.65); display: flex; align-items: center; transition: color .15s; }
    .prof-close:hover { color: #fff; }

    .prof-body { flex: 1; overflow-y: auto; padding: 22px 24px; display: flex; flex-direction: column; gap: 14px; }
    .prof-body::-webkit-scrollbar { width: 4px; }
    .prof-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

    .prof-field-group { display: flex; flex-direction: column; gap: 4px; }

    .prof-warning {
      display: flex; gap: 12px; align-items: flex-start;
      background: linear-gradient(135deg,#fff7f7,#fee2e2);
      border: 1.5px solid #fecaca; border-radius: 12px; padding: 14px 16px;
    }
    .prof-warning-icon { width: 38px; height: 38px; border-radius: 10px; background: #fff; border: 1.5px solid #fecaca; display: flex; align-items: center; justify-content: center; color: #dc2626; flex-shrink: 0; }
    .prof-warning-title { font-size: 13.5px; font-weight: 700; color: #991b1b; margin-bottom: 4px; }
    .prof-warning-desc  { font-size: 12.5px; color: #b91c1c; font-weight: 500; line-height: 1.55; }

    .prof-tip { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }

    .prof-footer { padding: 14px 24px; border-top: 1.5px solid #f1f5f9; background: #fafbfc; display: flex; align-items: center; justify-content: flex-end; gap: 10px; flex-shrink: 0; }
    .prof-btn-cancel {
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
      padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
      cursor: pointer; transition: all .15s;
    }
    .prof-btn-cancel:hover { background: #f1f5f9; }

    .prof-btn-submit--blue {
      display: inline-flex; align-items: center; gap: 7px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
      padding: 10px 18px; border-radius: 10px; border: none;
      background: linear-gradient(135deg,#1d4ed8,#2563eb);
      cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.28); transition: all .15s;
    }
    .prof-btn-submit--blue:hover  { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.36); }
    .prof-btn-submit--blue:disabled { opacity: .5; cursor: not-allowed; transform: none; }

    .prof-btn-submit--red {
      display: inline-flex; align-items: center; gap: 7px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
      padding: 10px 18px; border-radius: 10px; border: none;
      background: linear-gradient(135deg,#991b1b,#dc2626);
      cursor: pointer; box-shadow: 0 4px 14px rgba(220,38,38,.28); transition: all .15s;
    }
    .prof-btn-submit--red:hover  { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,.36); }
    .prof-btn-submit--red:disabled { opacity: .5; cursor: not-allowed; transform: none; }

    .prof-spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: profSpin .65s linear infinite; }
    @keyframes profSpin { to { transform: rotate(360deg); } }

    @media (max-width: 540px) { .prof-panel { max-width: 100%; } }
  `}</style>
);